'use client';

import { useEffect, useRef, useState } from 'react';

type Crop = { x: number; y: number; w: number; h: number };
type DragMode = 'move' | 'nw' | 'ne' | 'sw' | 'se';

const aspectPresets: Array<{ id: string; label: string; ratio: number | null }> = [
  { id: 'free', label: 'Free', ratio: null },
  { id: '1:1', label: '1:1', ratio: 1 },
  { id: '16:9', label: '16:9', ratio: 16 / 9 },
  { id: '4:3', label: '4:3', ratio: 4 / 3 },
  { id: '3:2', label: '3:2', ratio: 3 / 2 },
];

const MAX_DISPLAY = 520;
const MIN_CROP = 16;

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function getBaseName(name: string) {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(0, dot) : name;
}

export default function BrowserImageCropper() {
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [fileName, setFileName] = useState('image');
  const [natural, setNatural] = useState({ w: 0, h: 0 });
  const [scale, setScale] = useState(1);
  const [crop, setCrop] = useState<Crop>({ x: 0, y: 0, w: 0, h: 0 });
  const [aspectId, setAspectId] = useState('free');
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const scaleRef = useRef(1);
  const naturalRef = useRef({ w: 0, h: 0 });
  const dragRef = useRef<{ mode: DragMode; px: number; py: number; crop: Crop } | null>(null);

  const activeRatio = aspectPresets.find((preset) => preset.id === aspectId)?.ratio ?? null;

  useEffect(() => {
    return () => {
      if (downloadUrl) {
        URL.revokeObjectURL(downloadUrl);
      }
    };
  }, [downloadUrl]);

  function handleFile(file: File | undefined) {
    if (!file) return;
    setError(null);
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      const nextScale = Math.min(1, MAX_DISPLAY / Math.max(w, h));
      setImage(img);
      setNatural({ w, h });
      naturalRef.current = { w, h };
      setScale(nextScale);
      scaleRef.current = nextScale;
      setAspectId('free');
      setCrop({
        x: Math.round(w * 0.1),
        y: Math.round(h * 0.1),
        w: Math.round(w * 0.8),
        h: Math.round(h * 0.8),
      });
      setFileName(getBaseName(file.name) || 'image');
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      setError('This image format is not supported in your browser.');
      URL.revokeObjectURL(url);
    };
    img.src = url;
  }

  // Render the cropped preview to an offscreen canvas whenever the crop changes.
  useEffect(() => {
    if (!image || crop.w < 1 || crop.h < 1) return;
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(crop.w));
    canvas.height = Math.max(1, Math.round(crop.h));
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(image, crop.x, crop.y, crop.w, crop.h, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (blob) {
        setDownloadUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(blob);
        });
      }
    }, 'image/png');
  }, [image, crop]);

  function applyAspect(ratio: number | null, base: Crop): Crop {
    if (ratio === null) return base;
    const { w: nw, h: nh } = naturalRef.current;
    let width = base.w;
    let height = width / ratio;
    if (height > nh) {
      height = nh;
      width = height * ratio;
    }
    if (width > nw) {
      width = nw;
      height = width / ratio;
    }
    const x = clamp(base.x, 0, nw - width);
    const y = clamp(base.y, 0, nh - height);
    return { x, y, w: width, h: height };
  }

  function handleAspectChange(id: string) {
    setAspectId(id);
    const ratio = aspectPresets.find((preset) => preset.id === id)?.ratio ?? null;
    setCrop((prev) => applyAspect(ratio, prev));
  }

  function updateNumeric(patch: Partial<Crop>) {
    setCrop((prev) => {
      const { w: nw, h: nh } = naturalRef.current;
      let next = { ...prev, ...patch };
      next.w = clamp(next.w, MIN_CROP, nw);
      next.h = clamp(next.h, MIN_CROP, nh);
      next.x = clamp(next.x, 0, nw - next.w);
      next.y = clamp(next.y, 0, nh - next.h);
      if (activeRatio !== null && (patch.w !== undefined || patch.h !== undefined)) {
        next = applyAspect(activeRatio, next);
      }
      return next;
    });
  }

  function onPointerDown(mode: DragMode, event: React.PointerEvent) {
    event.preventDefault();
    (event.target as HTMLElement).setPointerCapture?.(event.pointerId);
    dragRef.current = { mode, px: event.clientX, py: event.clientY, crop: { ...crop } };
  }

  function onPointerMove(event: React.PointerEvent) {
    const drag = dragRef.current;
    if (!drag) return;
    const s = scaleRef.current || 1;
    const { w: nw, h: nh } = naturalRef.current;
    const dx = (event.clientX - drag.px) / s;
    const dy = (event.clientY - drag.py) / s;
    const start = drag.crop;

    if (drag.mode === 'move') {
      updateNumeric({
        x: clamp(start.x + dx, 0, nw - start.w),
        y: clamp(start.y + dy, 0, nh - start.h),
      });
      return;
    }

    let x = start.x;
    let y = start.y;
    let w = start.w;
    let h = start.h;
    const right = start.x + start.w;
    const bottom = start.y + start.h;

    if (drag.mode === 'nw') {
      x = clamp(start.x + dx, 0, right - MIN_CROP);
      y = clamp(start.y + dy, 0, bottom - MIN_CROP);
      w = right - x;
      h = bottom - y;
    } else if (drag.mode === 'ne') {
      y = clamp(start.y + dy, 0, bottom - MIN_CROP);
      w = clamp(start.w + dx, MIN_CROP, nw - start.x);
      h = bottom - y;
    } else if (drag.mode === 'sw') {
      x = clamp(start.x + dx, 0, right - MIN_CROP);
      w = right - x;
      h = clamp(start.h + dy, MIN_CROP, nh - start.y);
    } else {
      w = clamp(start.w + dx, MIN_CROP, nw - start.x);
      h = clamp(start.h + dy, MIN_CROP, nh - start.y);
    }

    if (activeRatio !== null) {
      h = w / activeRatio;
      if (drag.mode === 'nw' || drag.mode === 'ne') {
        y = bottom - h;
      }
      if (y < 0) {
        y = 0;
        h = bottom;
        w = h * activeRatio;
      }
      if (y + h > nh) {
        h = nh - y;
        w = h * activeRatio;
      }
    }

    setCrop({ x, y, w, h });
  }

  function onPointerUp() {
    dragRef.current = null;
  }

  const dispW = Math.round(natural.w * scale);
  const dispH = Math.round(natural.h * scale);
  const handleStyle: React.CSSProperties = {
    position: 'absolute',
    width: 14,
    height: 14,
    background: '#ffffff',
    border: '2px solid #2563eb',
    borderRadius: 3,
    touchAction: 'none',
  };

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Crop</span>
        <h2 className="tool-live-title">Crop an image</h2>
        <p className="tool-live-subtitle">
          Upload an image, drag the crop box or type exact coordinates, and download the cut. Runs
          entirely in your browser and nothing is uploaded to a server.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <label className="tool-live-field tool-live-field-full">
            <span>Choose an image</span>
            <input
              accept="image/*"
              onChange={(event) => handleFile(event.target.files?.[0])}
              type="file"
            />
          </label>

          {image ? (
            <div className="tool-live-controls">
              <label className="tool-live-field tool-live-field-full">
                <span>Aspect ratio</span>
                <select
                  onChange={(event) => handleAspectChange(event.target.value)}
                  value={aspectId}
                >
                  {aspectPresets.map((preset) => (
                    <option key={preset.id} value={preset.id}>
                      {preset.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="tool-live-field">
                <span>X (px)</span>
                <input
                  min={0}
                  onChange={(event) => updateNumeric({ x: Number(event.target.value) })}
                  type="number"
                  value={Math.round(crop.x)}
                />
              </label>
              <label className="tool-live-field">
                <span>Y (px)</span>
                <input
                  min={0}
                  onChange={(event) => updateNumeric({ y: Number(event.target.value) })}
                  type="number"
                  value={Math.round(crop.y)}
                />
              </label>
              <label className="tool-live-field">
                <span>Width (px)</span>
                <input
                  min={MIN_CROP}
                  onChange={(event) => updateNumeric({ w: Number(event.target.value) })}
                  type="number"
                  value={Math.round(crop.w)}
                />
              </label>
              <label className="tool-live-field">
                <span>Height (px)</span>
                <input
                  min={MIN_CROP}
                  onChange={(event) => updateNumeric({ h: Number(event.target.value) })}
                  type="number"
                  value={Math.round(crop.h)}
                />
              </label>
            </div>
          ) : null}

          <div className="tool-live-actions">
            {downloadUrl ? (
              <a
                className="button button-secondary"
                download={`${fileName}-cropped.png`}
                href={downloadUrl}
              >
                Download PNG
              </a>
            ) : null}
          </div>

          {error ? (
            <p className="status-message" style={{ color: '#dc2626' }}>
              {error}
            </p>
          ) : null}
          {image ? null : <p className="tool-live-panel-note">Pick an image to start cropping.</p>}
        </section>

        <section className="tool-live-preview">
          {image ? (
            <div
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              style={{
                position: 'relative',
                width: dispW,
                height: dispH,
                maxWidth: '100%',
                borderRadius: 12,
                overflow: 'hidden',
                touchAction: 'none',
                userSelect: 'none',
              }}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                alt="Crop source"
                draggable={false}
                src={image.src}
                style={{ width: dispW, height: dispH, display: 'block' }}
              />
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  boxShadow: `0 0 0 9999px rgba(0,0,0,0.45)`,
                  clipPath: `polygon(
                    0 0, 0 100%, ${crop.x * scale}px 100%, ${crop.x * scale}px ${crop.y * scale}px,
                    ${(crop.x + crop.w) * scale}px ${crop.y * scale}px,
                    ${(crop.x + crop.w) * scale}px ${(crop.y + crop.h) * scale}px,
                    ${crop.x * scale}px ${(crop.y + crop.h) * scale}px, ${crop.x * scale}px 100%,
                    100% 100%, 100% 0)`,
                  pointerEvents: 'none',
                }}
              />
              <div
                onPointerDown={(event) => onPointerDown('move', event)}
                style={{
                  position: 'absolute',
                  left: crop.x * scale,
                  top: crop.y * scale,
                  width: crop.w * scale,
                  height: crop.h * scale,
                  border: '1px solid #2563eb',
                  boxSizing: 'border-box',
                  cursor: 'move',
                  touchAction: 'none',
                }}
              >
                <div
                  onPointerDown={(event) => onPointerDown('nw', event)}
                  style={{ ...handleStyle, left: -7, top: -7, cursor: 'nwse-resize' }}
                />
                <div
                  onPointerDown={(event) => onPointerDown('ne', event)}
                  style={{ ...handleStyle, right: -7, top: -7, cursor: 'nesw-resize' }}
                />
                <div
                  onPointerDown={(event) => onPointerDown('sw', event)}
                  style={{ ...handleStyle, left: -7, bottom: -7, cursor: 'nesw-resize' }}
                />
                <div
                  onPointerDown={(event) => onPointerDown('se', event)}
                  style={{ ...handleStyle, right: -7, bottom: -7, cursor: 'nwse-resize' }}
                />
              </div>
            </div>
          ) : null}
          {image ? (
            <p className="tool-live-panel-note">
              Crop: {Math.round(crop.w)} × {Math.round(crop.h)} px from a {natural.w} × {natural.h}{' '}
              px image.
            </p>
          ) : null}
          <p className="tool-live-panel-note">
            Processing runs locally with an HTML canvas. Your image never leaves your device.
          </p>
        </section>
      </div>
    </div>
  );
}
